import { Box, Dialog, DialogContent, dialogContentClasses, Stack, SvgIconProps } from '@mui/material';
import { Close as CloseIcon } from 'mdi-material-ui';
import React, { forwardRef } from 'react';
import { translateSizeToMaterialSize } from '../../utils/translate-size';
import { IconButton } from '../IconButton';
import { Typography } from '../Typography';

type NativeDivProps = React.ComponentPropsWithoutRef<'div'>;

export type ModalSize = 'small' | 'medium' | 'large' | 'fullScreen';

export interface ModalBaseProps extends Omit<NativeDivProps, 'title'> {
  /**
   * Required to show the modal. Renders `null` if false.
   */
  open: boolean;
  /**
   * Badge shown to the right of the modal title. Useful for highlighting something about the content of the modal (e.g.
   * new feature, internal only). It is recommended to use a "small" badge for this use case.
   */
  badge?: React.ReactNode;
  /**
   * Content of the modal. Children are rendered as-is, with a single wrapping div for scrolling behavior.
   * Consumers can provide any additional styling as necessary to provide custom behavior inside the modal body.
   */
  children?: React.ReactNode;
  /**
   * Composable footer component. Any content is rendered as-is, with no assumptions on styling behavior. Use Modal
   * instead if you want only a single primary/secondary button.
   */
  Footer?: React.ReactNode;
  /**
   * When true, hides the default header. Useful when you want to provide a custom header.
   */
  hideHeader?: boolean;
  /**
   * Optional click handler that is called when the modal header X button is clicked, or user clicks outside of the
   * modal to close. Use this to control `open` state
   */
  onClose?: () => void;
  /**
   * Width of the modal. 'medium' is the default
   * */
  size?: ModalSize;
  /**
   * Title of the modal. Rendered in the modal header
   */
  title?: string;
  /**
   * An optional SVG icon that will appear to the left of the modal title
   */
  TitleIcon?: React.ElementType<SvgIconProps>;
}

/**
 * ModalBase is a raw implementation of a modal that allows custom composition of the header and Footer. You most likely
 * do not actually want to use this component, and should use <Modal /> instead. If you find yourself using ModalBase,
 * consult with your designer to make sure your usecase actually requires it, and ignoring the consistency provided by
 * Modal is actually required.
 */
export const ModalBase = forwardRef<HTMLDivElement, ModalBaseProps>(function ModalBase(
  { open, children, title, TitleIcon, Footer, hideHeader, onClose, size = 'medium', badge },
  ref
) {
  const hasHeader = !hideHeader && (!!title || !!onClose || !!badge);
  const hasFooter = !!Footer;

  return (
    <Dialog
      ref={ref}
      open={open}
      onClose={onClose}
      scroll="paper"
      keepMounted={false}
      fullWidth
      maxWidth={translateSizeToMaterialSize(size)}
      aria-labelledby="modal-title"
      fullScreen={size === 'fullScreen'}
    >
      {hasHeader && (
        <Box padding={(theme) => theme.spacing(1, 2)}>
          <Stack direction="row" spacing={1} alignItems="center">
            {TitleIcon && <TitleIcon fontSize="small" />}
            <Typography variant="h3" minHeight="24px" id="modal-title">
              {title}
            </Typography>
            {badge && (
              // Div needed around badge for vertical centering to work because badge is usually an inline
              // element.
              <div>{badge}</div>
            )}
          </Stack>

          {onClose && (
            <IconButton
              aria-label="Close"
              onClick={onClose}
              sx={(theme) => ({
                position: 'absolute',
                top: theme.spacing(0.5),
                right: theme.spacing(0.5),
              })}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      )}
      {/* Use sx override rather than overrides file, since we DO NOT want to remove padding for all users of DialogContent */}
      {children && (
        <DialogContent
          sx={{
            padding: 0,
            [`&.${dialogContentClasses.dividers}`]: {
              // Remove the bottom border "divider" when a footer is not present.
              borderBottom: !hasFooter ? 'none' : undefined,
            },
          }}
        >
          {children}
        </DialogContent>
      )}
      {Footer}
    </Dialog>
  );
});
